import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isTestimonialsPage = location.pathname === '/testimonials';
  const isQuizResultsPage = location.pathname === '/quiz-results-dashboard';
  const isQuizPage = location.pathname === '/edad-real-assessment-quiz';

  return (
    <footer>
      {/* CTA Section - At the top (hidden on testimonials, quiz, and quiz results pages) */}
      {!isTestimonialsPage && !isQuizResultsPage && !isQuizPage && (
        <div className="bg-primary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center">
              <h3 className="font-heading text-lg font-semibold text-primary-foreground mb-2">
                ¿Lista para Conocer Tu Edad Real?
              </h3>
              <p className="text-primary-foreground/80 text-sm mb-4">
                Descubre en 5 minutos por qué te sientes como te sientes. Miles de mujeres ya lo han hecho.
              </p>
              <Link
                to="/edad-real-assessment-quiz"
                className="inline-flex items-center space-x-2 bg-background text-foreground px-6 py-3 rounded-lg font-semibold hover:bg-background/90 transition-colors"
              >
                <span>Hacer Test Gratuito</span>
                <Icon name="ArrowRight" size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer Section - At the bottom */}
      <div className="bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

            {/* Logo and Description */}
            <div className="col-span-1 lg:col-span-2">
              <div className="flex items-center mb-4">
                <img
                  src="/assets/images/logo_comeyvivesano.webp"
                  alt="Come y Vive Sano - Coach de Hábitos Saludables y Bienestar"
                  className="h-14 w-auto"
                />
                {/* SEO-friendly hidden text */}
                <span className="sr-only">Come y Vive Sano</span>
              </div>
              <p className="text-background/80 mb-6 max-w-md">
                Transformamos vidas a través de hábitos saludables reales. Sin dietas milagrosas,
                sin promesas vacías. Solo resultados basados en ciencia y experiencia.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://instagram.com/comeyvivesano"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-background/10 hover:bg-background/20 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <Icon name="Instagram" size={20} className="text-background" />
                </a>
                <a
                  href="https://facebook.com/comeyvivesano"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-background/10 hover:bg-background/20 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                >
                  <Icon name="Facebook" size={20} className="text-background" />
                </a>
                <a
                  href="https://youtube.com/@comeyvivesano"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-background/10 hover:bg-background/20 rounded-full flex items-center justify-center transition-colors"
                  aria-label="YouTube"
                >
                  <Icon name="Youtube" size={20} className="text-background" />
                </a>
                <a
                  href="mailto:info@comeyvivesano.com"
                  className="w-12 h-12 bg-background/10 hover:bg-background/20 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Email"
                >
                  <Icon name="Mail" size={20} className="text-background" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-heading text-lg font-semibold mb-4">Enlaces Rápidos</h3>
              <nav className="space-y-3">
                <Link
                  to="/"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Inicio
                </Link>
                <Link
                  to="/edad-real-assessment-quiz"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Test de Edad Real
                </Link>
                <Link
                  to="/blog-content-hub"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Blog
                </Link>
                <Link
                  to="/testimonials"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Testimonios
                </Link>
                <Link
                  to="/contact-lead-capture"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Contacto
                </Link>
              </nav>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-heading text-lg font-semibold mb-4">Información Legal</h3>
              <nav className="space-y-3">
                <Link
                  to="/politica-privacidad"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Política de Privacidad
                </Link>
                <Link
                  to="/terminos-servicio"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Términos y Condiciones
                </Link>
                <Link
                  to="/aviso-legal"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Aviso Legal
                </Link>
                <Link
                  to="/politica-cookies"
                  className="block text-background/80 hover:text-background transition-colors"
                >
                  Política de Cookies
                </Link>
                <button
                  onClick={() => {
                    localStorage.removeItem('cookie_consent');
                    window.location.reload();
                  }}
                  className="block text-left text-background/80 hover:text-background transition-colors"
                >
                  Configurar Cookies
                </button>
              </nav>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-background/20 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">

              {/* Copyright */}
              <div className="text-background/60 text-sm text-center md:text-left">
                <p>© {currentYear} Miri Espejo Coach - Come y Vive Sano. Todos los derechos reservados.</p>
              </div>

              {/* Health Disclaimer */}
              <div className="text-background/60 text-xs text-center md:text-right max-w-md">
                <p>
                  Los consejos de este sitio no sustituyen el consejo médico profesional.
                  Consulta siempre con tu médico antes de realizar cambios significativos en tu dieta o ejercicio.
                </p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-6 text-center">
              <div className="flex flex-wrap justify-center items-center space-x-6 text-background/60 text-xs">
                <span className="flex items-center space-x-1">
                  <Icon name="Shield" size={12} />
                  <span>Datos protegidos por RGPD</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Lock" size={12} />
                  <span>Conexión segura SSL</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="MapPin" size={12} />
                  <span>España</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;